import { type DocumentNode } from '@apollo/client';
import {
  Button,
  ButtonColor,
  IconArrowRight,
  IconFolderPlus,
  IconGroup,
  IconHelp,
  IconPhone,
  IconSwitch,
  IconUserAdd,
  Modal,
  ModalContent,
  type ModalHook,
  ToastDuration,
  ToastType,
  useControlledModalState,
  useToast,
} from '@leland-dev/leland-ui-library';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { type FC, type SVGProps } from 'react';

import {
  type GoalName,
  type Scalars,
} from '../../../__generated-gql-types__/globalTypes';
import BannerImage from '../../../assets/images/request-to-work-with-modal-banner.png';
import {
  APPLICANT_SITE_URL,
  MEET_WITH_TEAM_MEMBER_CAL_URL,
} from '../../../utils/constants';
import { wasMutationSuccessful } from '../../../utils/mutations';

import {
  type RequestCoachMatchModalCoachFragment,
  useRequestCoachMatchModalMatchCoachToMembershipMutation,
} from './__generated-gql-types__/RequestCoachMatchModal.generated';

interface RequestCoachMatchModalHookProps {
  coach: RequestCoachMatchModalCoachFragment;
  goal: GoalName;
  applicantUrn: Scalars['ApplicantUrn']['output'];
  categoryUrn: string;
  refetchQueries?: DocumentNode[];
}

interface RequestCoachMatchModalProps extends RequestCoachMatchModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const RequestMatchProofItem: FC<{
  Icon: FC<SVGProps<SVGSVGElement>>;
  text: string;
  subtext?: string;
}> = ({ Icon, text, subtext }) => (
  <li className="flex items-center gap-3 py-3">
    <Icon className="size-6" />
    <div>
      <h5 className="text-xl font-medium">{text}</h5>
      {subtext ? <p className="text-leland-gray-light">{subtext}</p> : null}
    </div>
  </li>
);

const RequestCoachMatchModalContent: FC<RequestCoachMatchModalProps> = ({
  coach,
  goal,
  applicantUrn,
  categoryUrn,
  refetchQueries,
  open,
  setOpen,
}) => {
  const [matchCoachToMemberAction] =
    useRequestCoachMatchModalMatchCoachToMembershipMutation();
  const { showToast } = useToast();
  const router = useRouter();

  const matchCoachToMember = async (onSuccess?: () => void) => {
    const res = await matchCoachToMemberAction({
      variables: {
        matchedCoachInput: {
          applicantUrn: applicantUrn,
          coachUrn: coach.id,
          categoryUrn,
        },
      },
      refetchQueries,
    });
    if (wasMutationSuccessful(res)) {
      showToast({
        message: 'Your request has been sent!',
        type: ToastType.SUCCESS,
        duration: ToastDuration.NORMAL,
      });
      onSuccess?.();
    } else {
      showToast({
        message: 'Something went wrong. Please try again.',
        type: ToastType.ERROR,
        duration: ToastDuration.NORMAL,
      });
    }
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent>
        <div className="flex h-full flex-col overflow-hidden">
          <Image
            src={BannerImage}
            alt="Request to work with modal banner"
            height={177}
            width={693}
          />
          <div className="shrink overflow-scroll px-10 pt-8">
            <h3 className="text-3xl font-medium">
              Build your coaching plan to get started.
            </h3>
            <h4 className="mt-2 border-b border-leland-gray-stroke pb-6 text-2xl text-leland-gray-extra-light">
              Work with {coach.user.firstName} and thousands of other coaches.
            </h4>
            <p className="my-6 text-lg text-leland-gray-light">
              To get started working with {coach.user.firstName} or any of
              Leland’s coaches, you’ll need to purchase a coaching plan. If{' '}
              {coach.user.firstName} is unavailable, we’ll help you get matched
              to the perfect coach.
            </p>
            <ul>
              <RequestMatchProofItem
                Icon={IconUserAdd}
                text="Get matched with an expert coach"
              />
              <RequestMatchProofItem
                Icon={IconSwitch}
                text="Use your coaching plan on any coach within the tier you select"
              />
              <RequestMatchProofItem
                Icon={IconGroup}
                text="Enroll in exclusive group events taught by top coaches"
                subtext="Only available in some plans"
              />
              <RequestMatchProofItem
                Icon={IconFolderPlus}
                text="Full access to the Leland+ content library"
                subtext="Only available in some plans"
              />
              <RequestMatchProofItem
                Icon={IconHelp}
                text="Quick help from the Leland support team if you have any questions"
              />
            </ul>
          </div>
          <div className="flex gap-3 px-10 pb-8 pt-6">
            <Button
              label="Build my plan"
              RightIcon={IconArrowRight}
              buttonColor={ButtonColor.PRIMARY}
              onClick={async () => {
                await matchCoachToMember(() => {
                  setOpen(false);
                  void router.push(
                    `${APPLICANT_SITE_URL}/plan-summary?goal=${goal}&categoryId=${categoryUrn}&coachId=${coach.id}&isEditing=true`,
                  );
                });
              }}
            />
            <Button
              label="Talk to a team member"
              LeftIcon={IconPhone}
              onClick={async () => {
                await matchCoachToMember(() => {
                  setOpen(false);
                  void router.push(MEET_WITH_TEAM_MEMBER_CAL_URL);
                });
              }}
            />
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export const useRequestCoachMatchModal: ModalHook<
  RequestCoachMatchModalHookProps
> = () => {
  const [open, setOpen] = useControlledModalState();

  const RequestCoachMatchModal: FC<RequestCoachMatchModalHookProps> = (
    props,
  ) => (
    <RequestCoachMatchModalContent {...props} open={open} setOpen={setOpen} />
  );

  return {
    open,
    setOpen,
    Modal: RequestCoachMatchModal,
  };
};
